// PWA Install Prompt Component
// Shows custom install prompts for both iOS and Android
// Dismissible with localStorage persistence

import React, { useState, useEffect } from 'react';
import { isInstallAvailable, isInstalled, isIOS, isAndroid, promptInstall, onInstallAvailabilityChange, getIOSInstructions } from '../utils/pwaInstall';

const PWAInstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [showIOSInstructions, setShowIOSInstructions] = useState(false);
    const [installable, setInstallable] = useState(false);

    useEffect(() => {
        // Don't show if already installed
        if (isInstalled()) {
            return;
        }

        // Check if user has dismissed prompt
        const dismissed = localStorage.getItem('pwa_install_dismissed');
        const dismissedDate = localStorage.getItem('pwa_install_dismissed_date');
        
        // Show again after 7 days
        if (dismissed && dismissedDate) {
            const daysSinceDismissed = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < 7) {
                return;
            }
        }

        // Subscribe to install availability
        const unsubscribe = onInstallAvailabilityChange((available) => {
            setInstallable(available);
            if (available || isIOS()) {
                // Show prompt after a delay (not immediately on page load)
                setTimeout(() => setShowPrompt(true), 3000);
            }
        });

        return unsubscribe;
    }, []);

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa_install_dismissed', 'true');
        localStorage.setItem('pwa_install_dismissed_date', Date.now().toString());
    };

    const handleInstall = async () => {
        if (isAndroid() && installable) {
            // Android - show native prompt
            const accepted = await promptInstall();
            if (accepted) {
                setShowPrompt(false);
            }
        } else if (isIOS()) {
            // iOS - show instructions
            setShowIOSInstructions(true);
        }
    };

    if (!showPrompt || isInstalled()) {
        return null;
    }

    // iOS Instructions Modal
    if (showIOSInstructions) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
                 style={{
                     paddingTop: 'max(env(safe-area-inset-top), 16px)',
                     paddingBottom: 'max(env(safe-area-inset-bottom), 16px)'
                 }}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
                    <div className="text-center mb-6">
                        <div className="text-5xl mb-3">📱</div>
                        <h3 className="text-xl font-bold text-gray-900">Install SuperCalc Pro</h3>
                    </div>

                    <div className="space-y-4 mb-6">
                        {getIOSInstructions().map((instruction, idx) => (
                            <div key={idx} className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                                    {idx + 1}
                                </div>
                                <p className="text-gray-700 text-sm flex-1 pt-0.5">{instruction}</p>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-center mb-4">
                        <div className="text-center">
                            <div className="text-4xl mb-2">
                                <svg className="w-8 h-8 mx-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L11 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c-.25.78.232 1.607 1.07 1.836.813.217 1.643-.16 1.899-.932l.817-2.552c.243-.758-.232-1.604-1.067-1.832-.814-.216-1.642.16-1.901.928z" />
                                </svg>
                            </div>
                            <p className="text-xs text-gray-500">Tap Share, then "Add to Home Screen"</p>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowIOSInstructions(false)}
                        className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200"
                        style={{ minHeight: '48px', fontSize: '16px' }}
                    >
                        Got it
                    </button>
                </div>
            </div>
        );
    }

    // Install Prompt Banner
    return (
        <div className="fixed left-4 right-4 z-50 animate-slide-up"
             style={{ 
                 bottom: 'calc(72px + max(env(safe-area-inset-bottom), 0px))'
             }}>
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-2xl p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 text-3xl">📱</div>
                    <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base mb-1">Install SuperCalc Pro</h4>
                        <p className="text-sm text-blue-100 mb-3">
                            Add to your home screen for quick access and offline use
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={handleInstall}
                                className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50 active:bg-blue-100 transition-colors shadow-md"
                                style={{ minHeight: '40px', fontSize: '16px', touchAction: 'manipulation' }}
                            >
                                {isIOS() ? 'Show Me How' : 'Install'}
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="px-4 py-2 bg-blue-500 bg-opacity-50 text-white rounded-lg font-medium text-sm hover:bg-opacity-70 active:bg-opacity-80 transition-all"
                                style={{ minHeight: '40px', fontSize: '16px', touchAction: 'manipulation' }}
                            >
                                Maybe Later
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 text-white opacity-70 hover:opacity-100"
                        style={{ minWidth: '32px', minHeight: '32px' }}
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Add slide-up animation to global styles if not already present
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes slide-up {
            from {
                transform: translateY(100%);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }
        .animate-slide-up {
            animation: slide-up 0.3s ease-out;
        }
    `;
    if (!document.querySelector('style[data-pwa-animations]')) {
        style.setAttribute('data-pwa-animations', 'true');
        document.head.appendChild(style);
    }
}

export default PWAInstallPrompt;

